import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Stack,
  Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  Contacts as ContactsIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  AccountBalance as BankIcon,
  Email as EmailIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import { useUnifiedSync } from '../hooks/useUnifiedSync';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  fitTruncateSx,
  fitChipSx,
} from '../utils/fitTable';

interface RevolutCounterparty {
  id: number;
  user_id: number;
  revolut_id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  country: string | null;
  state: string | null;
  postcode: string | null;
  city: string | null;
  region: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  account_no: string | null;
  sort_code: string | null;
  iban: string | null;
  bic: string | null;
  routing_number: string | null;
  ifsc: string | null;
  clabe: string | null;
  bsb: string | null;
  bank_country: string | null;
  bank_name: string | null;
  bank_address_line_1: string | null;
  bank_address_line_2: string | null;
  bank_city: string | null;
  bank_region: string | null;
  bank_postcode: string | null;
  profile_type: string | null;
  revtag: string | null;
  account_holder_name: string | null;
  currency: string | null;
  created_by_request_id: string | null;
  account_source: string;
  created_at: string;
  updated_at: string;
}

interface CounterpartyResponse {
  counterparties: RevolutCounterparty[];
  pagination: {
    page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
}

const RevolutCounterparties: React.FC = () => {
  const [counterpartyData, setCounterpartyData] = useState<CounterpartyResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [profileTypeFilter, setProfileTypeFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCounterparty, setSelectedCounterparty] = useState<RevolutCounterparty | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Unified sync hook - counterparties sync is synchronous (no progress endpoint)
  const {
    syncStatus,
    isSyncing,
    startSync,
    error: syncError
  } = useUnifiedSync({
    dataType: 'counterparties',
    account: 'ireland',
    autoSyncOnMount: true,
    autoSyncDelay: 500
  });
  
  // Refresh data when sync completes
  useEffect(() => {
    if (syncStatus === 'completed') {
      setTimeout(() => {
        fetchCounterpartyData();
      }, 500);
    }
  }, [syncStatus]);

  const fetchCounterpartyData = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '50',
      });
      
      if (profileTypeFilter) {
        params.append('profile_type', profileTypeFilter);
      }
      
      if (countryFilter) {
        params.append('country', countryFilter);
      }

      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      const response = await api.get(`/api/revolut/counterparties?${params}`);
      setCounterpartyData(response.data);
    } catch (err: any) {
      console.error('Error fetching counterparty data:', err);
      setError(err.response?.data?.error || 'Failed to fetch counterparty data');
    } finally {
      setLoading(false);
    }
  };

  // Auto-sync is now handled by useUnifiedSync hook
  useEffect(() => {
    // Fetch existing data on mount
    fetchCounterpartyData();
  }, []); // Only run on mount

  // Unified sync handler - replaces old syncCounterparties function
  const handleUnifiedSync = async () => {
    await startSync();
  };

  useEffect(() => {
    fetchCounterpartyData();
  }, [page, profileTypeFilter, countryFilter, searchQuery]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getProfileTypeColor = (type: string | null) => {
    switch (type) {
      case 'personal':
        return 'primary';
      case 'business':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getProfileTypeIcon = (type: string | null) => {
    switch (type) {
      case 'personal':
        return <PersonIcon />;
      case 'business':
        return <BusinessIcon />;
      default:
        return <ContactsIcon />;
    }
  };

  const handleViewDetails = (counterparty: RevolutCounterparty) => {
    setSelectedCounterparty(counterparty);
    setDetailDialogOpen(true);
  };

  const formatAddress = (counterparty: RevolutCounterparty) => {
    const parts = [
      counterparty.address_line_1,
      counterparty.address_line_2,
      counterparty.city,
      counterparty.state,
      counterparty.postcode,
      counterparty.country
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  const formatBankInfo = (counterparty: RevolutCounterparty) => {
    const accountInfo = [counterparty.iban, counterparty.account_no, counterparty.sort_code]
      .filter(Boolean);
    
    if (accountInfo.length > 0) {
      return accountInfo[0];
    }
    
    return counterparty.bank_name || 'N/A';
  };

  if (loading && !counterpartyData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading counterparties...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
          <ContactsIcon sx={{ mr: 2 }} />
          Revolut Counterparties
        </Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            onClick={handleUnifiedSync}
            disabled={isSyncing || loading}
            sx={{ mr: 2 }}
          >
            {isSyncing ? 'Syncing...' : 'Sync Counterparties'}
          </Button>
          <Button
            variant="outlined"
            onClick={fetchCounterpartyData}
            startIcon={<RefreshIcon />}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {counterpartyData && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary">
                  Total Counterparties
                </Typography>
                <Typography variant="h4">
                  {counterpartyData.pagination.total_count.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All Accounts
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="secondary">
                  Personal
                </Typography>
                <Typography variant="h4">
                  {counterpartyData.counterparties.filter(c => c.profile_type === 'personal').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Current Page
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="info.main">
                  Business
                </Typography>
                <Typography variant="h4">
                  {counterpartyData.counterparties.filter(c => c.profile_type === 'business').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Current Page
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                placeholder="Search by name, email, or account"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Profile Type</InputLabel>
                <Select
                  value={profileTypeFilter}
                  label="Profile Type"
                  onChange={(e) => setProfileTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="personal">Personal</MenuItem>
                  <MenuItem value="business">Business</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Country</InputLabel>
                <Select
                  value={countryFilter}
                  label="Country"
                  onChange={(e) => setCountryFilter(e.target.value)}
                >
                  <MenuItem value="">All Countries</MenuItem>
                  <MenuItem value="IE">Ireland</MenuItem>
                  <MenuItem value="GB">United Kingdom</MenuItem>
                  <MenuItem value="US">United States</MenuItem>
                  <MenuItem value="FR">France</MenuItem>
                  <MenuItem value="DE">Germany</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Counterparties Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Counterparties ({counterpartyData?.pagination.total_count || 0} total)
          </Typography>
          
          <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
            <Table size="small" sx={FIT_TABLE_SX}>
              <FitColgroup
                cols={[
                  { key: 'type', pct: 12 },
                  { key: 'name' },
                  { key: 'contact', pct: 20 },
                  { key: 'location', pct: 17 },
                  { key: 'bank_details', pct: 17 },
                  { key: 'created', pct: 11 },
                  { key: 'actions', pct: 7 },
                ]}
              />
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Bank Details</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {counterpartyData?.counterparties.map((counterparty) => (
                  <TableRow key={counterparty.id}>
                    <TableCell>
                      <Chip 
                        icon={getProfileTypeIcon(counterparty.profile_type)}
                        label={counterparty.profile_type || 'Unknown'} 
                        color={getProfileTypeColor(counterparty.profile_type) as any}
                        size="small"
                        sx={fitChipSx}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ ...fitTruncateSx, fontWeight: 'medium' }} title={counterparty.name || ''}>
                        {counterparty.name || 'Unnamed'}
                      </Typography>
                      {counterparty.revtag && (
                        <Typography variant="caption" color="text.secondary" sx={fitTruncateSx}>
                          Revtag: {counterparty.revtag}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        {counterparty.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                            <EmailIcon sx={{ fontSize: 14, mr: 1, color: 'text.secondary' }} />
                            <Typography variant="caption" sx={{ ...fitTruncateSx, minWidth: 0 }} title={counterparty.email || ''}>
                              {counterparty.email}
                            </Typography>
                          </Box>
                        )}
                        {counterparty.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PhoneIcon sx={{ fontSize: 14, mr: 1, color: 'text.secondary' }} />
                            <Typography variant="caption">
                              {counterparty.phone}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        <LocationIcon sx={{ fontSize: 14, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="caption" sx={{ ...fitTruncateSx, minWidth: 0 }} title={formatAddress(counterparty)}>
                          {formatAddress(counterparty)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        <BankIcon sx={{ fontSize: 14, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="caption" sx={{ ...fitTruncateSx, minWidth: 0 }} title={formatBankInfo(counterparty) || undefined}>
                          {formatBankInfo(counterparty)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={fitTruncateSx}>
                        {formatDate(counterparty.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell className="fit-table-no-clip">
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewDetails(counterparty)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {counterpartyData?.pagination && counterpartyData.pagination.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={counterpartyData.pagination.total_pages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Counterparty Details Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Counterparty Details</DialogTitle>
        <DialogContent>
          {selectedCounterparty && (
            <Box>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {getProfileTypeIcon(selectedCounterparty.profile_type)}
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {selectedCounterparty.name || 'Unnamed Counterparty'}
                    </Typography>
                    <Chip 
                      label={selectedCounterparty.profile_type || 'Unknown'} 
                      color={getProfileTypeColor(selectedCounterparty.profile_type) as any}
                      sx={{ ml: 2 }}
                    />
                  </Box>
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
                    Contact Information
                  </Typography>
                  <Stack spacing={1}>
                    {selectedCounterparty.email && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Email:</Typography>
                        <Typography variant="body1">{selectedCounterparty.email}</Typography>
                      </Box>
                    )}
                    {selectedCounterparty.phone && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Phone:</Typography>
                        <Typography variant="body1">{selectedCounterparty.phone}</Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
                    Address
                  </Typography>
                  <Typography variant="body1">
                    {formatAddress(selectedCounterparty)}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
                    Bank Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      {selectedCounterparty.iban && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">IBAN:</Typography>
                          <Typography variant="body1">{selectedCounterparty.iban}</Typography>
                        </Box>
                      )}
                      {selectedCounterparty.account_no && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">Account Number:</Typography>
                          <Typography variant="body1">{selectedCounterparty.account_no}</Typography>
                        </Box>
                      )}
                      {selectedCounterparty.sort_code && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">Sort Code:</Typography>
                          <Typography variant="body1">{selectedCounterparty.sort_code}</Typography>
                        </Box>
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      {selectedCounterparty.bank_name && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">Bank Name:</Typography>
                          <Typography variant="body1">{selectedCounterparty.bank_name}</Typography>
                        </Box>
                      )}
                      {selectedCounterparty.bic && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">BIC:</Typography>
                          <Typography variant="body1">{selectedCounterparty.bic}</Typography>
                        </Box>
                      )}
                      {selectedCounterparty.currency && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">Currency:</Typography>
                          <Typography variant="body1">{selectedCounterparty.currency}</Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Raw Data</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(selectedCounterparty, null, 2)}
                    </pre>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RevolutCounterparties;
